import type { SortingState } from '@tanstack/react-table'
import { z } from 'zod'

import { PAGE_DEFAULT_LIMIT, PAGE_SIZES } from '@/shared/api/types'

/**
 * Peças comuns dos search params de QUALQUER listagem.
 *
 * Cada tela tem seus próprios filtros, mas paginação e ordenação são idênticas
 * em todas as 13. Antes de existir este módulo, cada tela reescrevia
 * `pagina`/`linhas` e a conversão `ordenar ⇄ SortingState` na mão — e as cópias
 * já tinham divergido (a lista de `linhas` do schema de schedules não batia com
 * a do paginador, e metade das telas passava `onSortingChange={() => undefined}`
 * com header clicável que não fazia nada).
 *
 * As chaves ficam em pt-BR porque a URL é superfície de produto (o usuário
 * copia e manda no WhatsApp) — mesma decisão de features/schedules/search.ts.
 */

/** `?linhas=` — allowlist derivada de PAGE_SIZES, nunca uma cópia literal. */
export const linhasSchema = z.literal(PAGE_SIZES).default(PAGE_DEFAULT_LIMIT)

/** `?pagina=` — base 1, como o usuário lê. O offset é problema do mapper. */
export const paginaSchema = z.int().min(1).default(1)

/**
 * Fragmento para espalhar no `z.object` da tela:
 * `z.object({ ...paginacaoSearchFields, ...meusFiltros })`
 */
export const paginacaoSearchFields = {
  pagina: paginaSchema,
  linhas: linhasSchema,
}

export interface PaginacaoSearch {
  readonly pagina: number
  readonly linhas: number
}

/**
 * `{ pagina, linhas }` → `{ limit, offset }` do backend.
 *
 * Base 1 na URL e offset base 0 no request é a conversão que mais dá errado
 * quando escrita à mão em 13 lugares (o clássico é esquecer o `- 1` e a página
 * 1 pular as primeiras 50 linhas).
 */
export function toLimitOffset(s: PaginacaoSearch): { limit: number; offset: number } {
  return { limit: s.linhas, offset: (s.pagina - 1) * s.linhas }
}

/**
 * `?ordenar=` (contrato do backend: `campo` ou `-campo`) → estado da tabela.
 *
 * O `-` é removido com regex ANCORADA, não `.replace('-', '')`: o replace solto
 * come o primeiro hífen ONDE QUER que ele esteja, então um campo futuro com
 * hífen no nome viraria outro campo silenciosamente.
 */
export function toSortingState(ordenar: string): SortingState {
  return [{ id: ordenar.replace(/^-/, ''), desc: ordenar.startsWith('-') }]
}

/**
 * Estado da tabela → `?ordenar=`.
 *
 * `fallback` é o default do endpoint: destravar a ordenação (3º clique no
 * header) não pode virar `order_by` vazio, que o backend responderia com a
 * ordem default dele — e a seta na tela ficaria mentindo.
 */
export function fromSortingState<T extends string>(s: SortingState, fallback: T): T {
  const first = s[0]
  if (first === undefined) return fallback
  return (first.desc ? `-${first.id}` : first.id) as T
}
